import {
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  formatNumber,
  getSmsBalance,
  smsBalanceStatus,
  useEffect,
  useState,
  XMarkIcon,
} from "@";
import { memo } from "react";

const SmsBalanceAlert = ({ isShow }) => {
  const [smsBalance, setSmsBalance] = useState(null);
  const [isSmsBalanceLow, setIsSmsBalanceLow] = useState(false);
  const [showSmsBalance, setShowSmsBalance] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchSmsBalance = async () => {
      const { data: sms } = await getSmsBalance();
      if (isMounted) {
        const status = smsBalanceStatus(sms.amount);
        setIsSmsBalanceLow(status);
        setSmsBalance(sms.amount || 0);
        isShow(status);
      }
    };

    fetchSmsBalance();

    return () => {
      isMounted = false;
    };
  }, []);

  // Shared toast wrapper class
  const toastWrapper =
    "fixed end-1/2 top-3 z-50 mb-4 w-[95%] -translate-x-1/2 sm:w-fit animate-[smsAlertIn_0.3s_ease-out]";

  return (
    <>
      {/* Low balance — warning */}
      {isSmsBalanceLow && (
        <div className={toastWrapper}>
          <div className="relative flex items-start gap-3 overflow-hidden rounded-2xl border border-amber-100 bg-white/95 px-4 py-3.5 ps-5 shadow-xl shadow-amber-900/5 backdrop-blur-md dark:border-amber-900/40 dark:bg-gray-800/95 dark:shadow-black/30">
            {/* Start-side accent stripe */}
            <span
              aria-hidden
              className="pointer-events-none absolute start-0 top-0 h-full w-1 bg-gradient-to-b from-amber-400 to-amber-600 dark:from-amber-500 dark:to-amber-700"
            />

            {/* Icon chip with pulse */}
            <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-md shadow-amber-700/25 ring-1 ring-amber-500/20 dark:from-amber-500 dark:to-amber-700">
              <span
                aria-hidden
                className="absolute inset-0 animate-ping rounded-2xl bg-amber-400 opacity-30"
              />
              <ExclamationTriangleIcon className="relative h-5 w-5" />
            </span>

            <div className="relative flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-400">
                تنبيه
              </p>
              <h3 className="text-base font-bold tracking-tight text-amber-900 dark:text-amber-100 md:text-lg">
                النقاط غير كافية
              </h3>
              <p className="mt-0.5 text-sm leading-relaxed text-amber-800/80 dark:text-amber-200/80">
                ليس لديك نقاط كافية لإرسال الرسائل. الرجاء التواصل مع الدعم
                الفني.
              </p>
              <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-bold tabular-nums text-amber-900 ring-1 ring-amber-200 dark:bg-amber-950/60 dark:text-amber-200 dark:ring-amber-800/40">
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 animate-pulse rounded-full bg-current"
                />
                المتبقي: {formatNumber(smsBalance ?? 0)}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setIsSmsBalanceLow(false)}
              aria-label="إغلاق"
              className="relative ms-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl border border-amber-200 bg-white/60 text-amber-700 transition-all duration-200 hover:rotate-90 hover:bg-amber-100 active:scale-95 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-900/40"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* OK balance — success */}
      {!isSmsBalanceLow && showSmsBalance && smsBalance && (
        <div className={toastWrapper}>
          <div className="relative flex items-center gap-3 overflow-hidden rounded-2xl border border-emerald-100 bg-white/95 px-4 py-3 ps-5 shadow-xl shadow-emerald-900/5 backdrop-blur-md dark:border-emerald-900/40 dark:bg-gray-800/95 dark:shadow-black/30">
            {/* Start-side accent stripe */}
            <span
              aria-hidden
              className="pointer-events-none absolute start-0 top-0 h-full w-1 bg-gradient-to-b from-emerald-400 to-emerald-600 dark:from-emerald-500 dark:to-emerald-700"
            />

            {/* Icon chip */}
            <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-md shadow-emerald-700/25 ring-1 ring-emerald-600/20 dark:from-emerald-500 dark:to-emerald-700">
              <ChatBubbleLeftRightIcon className="h-5 w-5" />
            </span>

            <div className="relative flex-1">
              <p className="text-sm font-medium tracking-tight text-emerald-900 dark:text-emerald-100">
                النقاط المتبقة للرسائل
              </p>
              <p className="text-base font-bold tabular-nums tracking-tight text-emerald-700 dark:text-emerald-300">
                {formatNumber(smsBalance)}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setShowSmsBalance(false)}
              aria-label="إغلاق"
              className="relative ms-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl border border-emerald-200 bg-white/60 text-emerald-700 transition-all duration-200 hover:rotate-90 hover:bg-emerald-100 active:scale-95 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-900/40"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes smsAlertIn { from { opacity: 0; transform: translate(-50%, -8px); } to { opacity: 1; transform: translate(-50%, 0); } }`}</style>
    </>
  );
};

export default memo(SmsBalanceAlert);
